<template>
  <div class="date-group">
    <div class="date-header" :class="{ 'is-stuck': isStuck }" ref="dateHeader">
      <div class="header-content">
        <div class="date-text">
          <Icon type="ios-calendar-outline" size="14" class="date-icon" />
          <span class="date-title">{{ formatDate(items[0].datescan) }}</span>
        </div>
        <span class="count-badge">
          <Icon type="ios-images-outline" size="12" />
          <span>{{ items.length }}</span>
        </span>
      </div>
    </div>
    
    <div class="photo-grid">
      <attendance-photo-card
        v-for="(item, index) in sortedItems"
        :key="`${item.employee_id}-${item.timescan}-${index}`"
        :attendance="item"
        @open-viewer="handleOpenViewer(index)"
      />
    </div>
  </div>
</template>

<script>
import { orderBy } from 'lodash'
import moment from 'moment'
import AttendancePhotoCard from './AttendancePhotoCard.vue'
import Vue from 'vue'
import 'viewerjs/dist/viewer.css'
import VueViewer from 'v-viewer'

Vue.use(VueViewer, {
  defaultOptions: {
    zIndex: 9999,
    inline: false,
    button: true,
    navbar: true,
    title: true,
    toolbar: true,
    tooltip: true,
    movable: true,
    zoomable: true,
    rotatable: true,
    scalable: true,
    transition: true,
    fullscreen: true,
    keyboard: true
  }
})

export default {
  name: 'AttendanceDateGroup',
  components: {
    AttendancePhotoCard
  },
  props: {
    date: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isStuck: false,
      observer: null
    }
  },
  computed: {
    sortedItems() {
      return orderBy(this.items, [
        (item) => {
          // Convert time string to 24-hour format for proper sorting
          const time = moment(item.timescan, ['HH:mm:ss', 'hh:mm A', 'HH:mm'])
          return time.isValid() ? time.valueOf() : 0
        }
      ], ['desc'])
    }
  },
  methods: {
    formatDate(dateStr) {
      return moment(dateStr, 'DD-MMM-YYYY').format('ddd, MMM DD, YYYY')
    },
    handleOpenViewer(index) {
      const images = this.sortedItems.map(item => {
        if (item.url) {
          return `/api/files?filePath=${item.url}`
        }
        return require('@/assets/default_user.jpg')
      })
      
      this.$viewerApi({
        images: images,
        options: {
          initialViewIndex: index,
          toolbar: true,
          navbar: true,
          title: (image, imageData) => {
            const item = this.sortedItems[imageData.index]
            return `${item.name} - ${item.timescan}`
          }
        }
      })
    },
    setupStickyObserver() {
      const sentinel = document.createElement('div')
      sentinel.style.position = 'absolute'
      sentinel.style.top = '0'
      sentinel.style.height = '1px'
      sentinel.style.width = '1px'
      sentinel.style.visibility = 'hidden'
      
      this.$el.insertBefore(sentinel, this.$refs.dateHeader)
      
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.isStuck = !entry.isIntersecting
        },
        { threshold: [1], rootMargin: '-145px 0px 0px 0px' }
      )
      
      this.observer.observe(sentinel)
    }
  },
  mounted() {
    this.setupStickyObserver()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped lang="scss">
.date-group {
  margin-bottom: 16px;
  position: relative;
  
  .date-header {
    background: #f7f8fa;
    border-bottom: 1px solid #e8eaec;
    position: sticky;
    top: 236px;
    z-index: 8;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 16px;
      transition: all 0.3s ease;
      
      @media (max-width: 768px) {
        padding: 6px 12px;
      }
    }
    
    .date-text {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .date-icon {
        color: #667eea;
        flex-shrink: 0;
      }
      
      .date-title {
        font-size: 13px;
        font-weight: 600;
        color: #17233d;
        letter-spacing: 0.2px;
        
        @media (max-width: 768px) {
          font-size: 12px;
        }
      }
    }
    
    .count-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 10px;
      background: rgba(102, 126, 234, 0.1);
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      color: #667eea;
      transition: all 0.2s ease;
      
      i {
        flex-shrink: 0;
      }
      
      @media (max-width: 768px) {
        font-size: 10px;
        padding: 2px 8px;
      }
    }
    
    &.is-stuck {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(12px) saturate(180%);
      box-shadow: 0 2px 12px rgba(102, 126, 234, 0.15);
      border-bottom: 2px solid #667eea;
      
      .header-content {
        padding: 6px 16px;
        
        @media (max-width: 768px) {
          padding: 5px 12px;
        }
      }
      
      .date-text {
        .date-title {
          font-size: 12px;
          
          @media (max-width: 768px) {
            font-size: 11px;
          }
        }
      }
      
      .count-badge {
        box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
      }
    }
  }
  
  .photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
    padding: 12px;
    background: #ffffff;
    
    @media (max-width: 1400px) {
      grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    }
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    }
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
      gap: 8px;
      padding: 8px;
    }
    
    @media (max-width: 480px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 6px;
      padding: 6px;
    }
  }
}

::v-deep .viewer-backdrop {
  background-color: rgba(0, 0, 0, 0.9);
}

::v-deep .viewer-title {
  color: white;
  font-size: 16px;
  font-weight: 600;
}

::v-deep .viewer-toolbar > li {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
}

::v-deep .viewer-button {
  background-color: rgba(255, 255, 255, 0.1);
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
}
</style>
